import { Suspense, lazy, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import FacebookSDK from "./components/auth/FacebookSDK";
import { CartProvider } from "@/contexts/CartContext";
import { UploadProvider } from "@/contexts/UploadContext";
import UploadProgressIndicator from "@/components/upload/UploadProgressIndicator";
import Layout from "@/components/layout/Layout";
import { usePerformanceTrace } from "./hooks/usePerformance"; // Import performance hook
import { app, analytics } from "./lib/firebase"; // Import Firebase app and analytics only
import {
  initServiceWorker,
  createUserSession,
  resumePendingUploads,
} from "./lib/session-utils";

// Import loader and skeleton components
import {
  PageLoader,
  AlbumsLoader,
  PhotosLoader,
  ProfileLoader,
  SettingsLoader,
  UploadLoader,
  AlbumsSkeleton,
  PhotoGridSkeleton,
  ProfileSkeleton,
  SettingsSkeleton,
  UploadSkeleton,
  SavedItemsSkeleton,
  PhotoDetailSkeleton,
  SearchResultsSkeleton,
} from "@/components/ui/loading-states";
import { LocationProvider } from "./contexts/LocationContext";

// Keep core/common components as static imports
// Only the page components should be lazy loaded

// Lazy load all page components
const Photos = lazy(() => import("./pages/Photos"));
const UploadPhoto = lazy(() => import("./pages/UploadPhoto"));
const PhotoHub = lazy(() => import("./pages/PhotoHub"));
const MyPhotos = lazy(() => import("./pages/MyPhotos"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AlbumView = lazy(() => import("./pages/AlbumView"));
const EditAlbum = lazy(() => import("./pages/EditAlbum"));
const AddPhotosToAlbum = lazy(() => import("./pages/AddPhotosToAlbum"));
const Profile = lazy(() => import("./pages/Profile"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AlbumsPage = lazy(() => import("./pages/Albums"));
const CreateAlbumPage = lazy(() => import("./pages/CreateAlbum"));
const MyAlbums = lazy(() => import("./pages/MyAlbums"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy"));
const DataDeletionPage = lazy(() => import("./pages/DeleteData"));
// const AdminPage = lazy(() => import("./pages/AdminPage"));
const BusinessCardPage = lazy(() => import("./pages/BusinessCardPage"));
import { StoragePermissionTester } from "@/components/test/StoragePermissionTester";
import { EdgeFunctionTester } from "./components/test/EdgeFunctionTester";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});
// Service Worker Initialization Component
const ServiceWorkerInitializer = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      // Initialize the service worker
      const swInitialized = await initServiceWorker();

      if (swInitialized) {
        // Create or restore user session
        await createUserSession();

        // Resume any pending uploads
        await resumePendingUploads();
      }

      setIsInitialized(true);
    };

    initialize();
  }, []);

  // Render nothing, this is just for initialization
  return null;
};

// Create a separate performance monitoring component to track route changes
const PerformanceMonitor = () => {
  const location = useLocation();
  const { startTrace, stopTrace, recordMetric, isRunning } =
    usePerformanceTrace("route_change", false);
  const [previousPath, setPreviousPath] = useState<string | null>(null);
  const [routeChangeStartTime, setRouteChangeStartTime] = useState<
    number | null
  >(null);

  // Track route changes
  useEffect(() => {
    // If there was a previous path, this is a route change
    if (previousPath !== null && previousPath !== location.pathname) {
      // Record the completion of previous route change
      if (routeChangeStartTime !== null) {
        const routeChangeTime = Math.round(
          window.performance.now() - routeChangeStartTime
        );
        recordMetric("route_change_time_ms", routeChangeTime);
        recordMetric("from_route", previousPath.length);
        recordMetric("to_route", location.pathname.length);
      }

      // Start new trace for this route
      if (isRunning) {
        stopTrace(); // Stop any existing trace
      }
      startTrace();
      setRouteChangeStartTime(window.performance.now());

      // Record the new route
      recordMetric("route_changed", 1);
    } else if (previousPath === null) {
      // First render, start timing
      startTrace();
      setRouteChangeStartTime(window.performance.now());
    }

    // Update previous path for next change
    setPreviousPath(location.pathname);

    // Stop the trace when the component unmounts
    return () => {
      if (isRunning) {
        stopTrace();
      }
    };
  }, [
    location.pathname,
    startTrace,
    stopTrace,
    recordMetric,
    previousPath,
    routeChangeStartTime,
    isRunning,
  ]);

  return null; // This component doesn't render anything
};

// Create a wrapper for lazy-loaded components to track their load times
const PerformanceTracedSuspense = ({
  children,
  fallback,
  name,
}: {
  children: React.ReactNode;
  fallback: React.ReactNode;
  name: string;
}) => {
  const { startTrace, stopTrace, recordMetric, isRunning } =
    usePerformanceTrace(`lazy_load_${name}`, false);
  const [loadStartTime] = useState<number>(() => window.performance.now());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isRunning) {
      startTrace();
    }

    return () => {
      if (isRunning) {
        stopTrace();
      }
    };
  }, [startTrace, stopTrace, isRunning]);

  // Effect that runs after the component renders
  useEffect(() => {
    if (!isLoaded) {
      const loadTime = Math.round(window.performance.now() - loadStartTime);
      recordMetric("component_load_time_ms", loadTime);
      setIsLoaded(true);

      // Record that the fallback was shown
      recordMetric("fallback_shown", 1);
    }
  }, [children, isLoaded, loadStartTime, recordMetric]);

  return <Suspense fallback={fallback}>{children}</Suspense>;
};

// Main app component with performance monitoring
const AppWithPerformance = () => {
  const { startTrace, stopTrace, recordMetric, isRunning } =
    usePerformanceTrace("app_startup", true);
  const [appStartTime] = useState<number>(() => window.performance.now());

  // Start measuring app startup time
  useEffect(() => {
    // Record when the app first renders
    const initialRenderTime = Math.round(
      window.performance.now() - appStartTime
    );
    recordMetric("initial_render_time_ms", initialRenderTime);

    // Create a timeout to measure time to interactive
    const timeToInteractiveTimeout = setTimeout(() => {
      const timeToInteractive = Math.round(
        window.performance.now() - appStartTime
      );
      recordMetric("time_to_interactive_ms", timeToInteractive);
    }, 0);

    // Setup performance observer for largest contentful paint
    if ("PerformanceObserver" in window) {
      try {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          if (entries.length > 0) {
            const lastEntry = entries[entries.length - 1];
            const lcp = Math.round(lastEntry.startTime);
            recordMetric("largest_contentful_paint_ms", lcp);
          }
        });

        lcpObserver.observe({
          type: "largest-contentful-paint",
          buffered: true,
        });

        return () => {
          lcpObserver.disconnect();
          clearTimeout(timeToInteractiveTimeout);
        };
      } catch (e) {
        console.error("LCP observer error:", e);
      }
    }

    return () => {
      clearTimeout(timeToInteractiveTimeout);
      if (isRunning) {
        stopTrace();
      }
    };
  }, [appStartTime, recordMetric, stopTrace, isRunning]);

  return (
    <PayPalScriptProvider
      options={{
        clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID,
        "disable-funding": "card,credit,paylater",
        currency: "USD",
      }}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <LocationProvider>
                <UploadProvider>
                  <TooltipProvider>
                    <FacebookSDK />
                    <Toaster />
                    <Sonner />
                    {/* Initialize the service worker */}
                    <ServiceWorkerInitializer />
                    {/* Add our enhanced upload progress indicator */}
                    <UploadProgressIndicator />
                    {/* Add the performance monitor */}
                    <PerformanceMonitor />
                    <Routes>
                      {/* Public routes outside the main layout */}
                      <Route
                        path="/auth/callback"
                        element={
                          <PerformanceTracedSuspense
                            fallback={
                              <PageLoader message="Processing authentication..." />
                            }
                            name="auth_callback"
                          >
                            <AuthCallback />
                          </PerformanceTracedSuspense>
                        }
                      />

                      {/* Landing page with its special layout */}
                      <Route
                        path="/"
                        element={
                          <Layout variant="landing" showNav={true}>
                            <PerformanceTracedSuspense
                              fallback={
                                <PageLoader
                                  type="default"
                                  message="Loading SurfSnap..."
                                />
                              }
                              name="landing_page"
                            >
                              <LandingPage />
                            </PerformanceTracedSuspense>
                          </Layout>
                        }
                      />

                      {/* Routes that share the default layout */}
                      <Route
                        path="/"
                        element={<Layout variant="default" showNav={true} />}
                      >
                        {/* Existing routes */}
                        <Route
                          path="/profile/:userId?"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={<ProfileSkeleton />}
                                name="profile"
                              >
                                <Profile />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={<SettingsSkeleton />}
                                name="settings"
                              >
                                <SettingsPage />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />

                        {/* Album related routes */}
                        <Route
                          path="/albums"
                          element={
                            <PerformanceTracedSuspense
                              fallback={<AlbumsSkeleton />}
                              name="albums"
                            >
                              <AlbumsPage />
                            </PerformanceTracedSuspense>
                          }
                        />
                        <Route
                          path="/albums/new"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={
                                  <PageLoader
                                    type="album"
                                    message="Preparing new album..."
                                  />
                                }
                                name="create_album"
                              >
                                <CreateAlbumPage />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/albums/:albumId"
                          element={
                            <PerformanceTracedSuspense
                              fallback={<PhotoGridSkeleton />}
                              name="album_view"
                            >
                              <AlbumView />
                            </PerformanceTracedSuspense>
                          }
                        />
                        <Route
                          path="/albums/:albumId/edit"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={
                                  <PageLoader
                                    type="album"
                                    message="Loading album editor..."
                                  />
                                }
                                name="edit_album"
                              >
                                <EditAlbum />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/albums/:albumId/add-photos"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={<PhotoGridSkeleton />}
                                name="add_photos_album"
                              >
                                <AddPhotosToAlbum />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />

                        {/* Existing photo routes */}
                        <Route
                          path="/photos"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={<PhotoGridSkeleton />}
                                name="photos"
                              >
                                <Photos />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/upload"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={<UploadSkeleton />}
                                name="upload"
                              >
                                <UploadPhoto />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/photo-hub"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={<SearchResultsSkeleton />}
                                name="photo_hub"
                              >
                                <PhotoHub />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/my-photos"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={<PhotoGridSkeleton />}
                                name="my_photos"
                              >
                                <MyPhotos />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/my-albums"
                          element={
                            <ProtectedRoute>
                              <PerformanceTracedSuspense
                                fallback={<AlbumsSkeleton />}
                                name="my_albums"
                              >
                                <MyAlbums />
                              </PerformanceTracedSuspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/privacy-policy"
                          element={
                            <PerformanceTracedSuspense
                              fallback={
                                <PageLoader message="Loading privacy policy..." />
                              }
                              name="privacy_policy"
                            >
                              <PrivacyPolicyPage />
                            </PerformanceTracedSuspense>
                          }
                        />
                        <Route
                          path="/data-deletion"
                          element={
                            <PerformanceTracedSuspense
                              fallback={
                                <PageLoader message="Loading data deletion information..." />
                              }
                              name="data_deletion"
                            >
                              <DataDeletionPage />
                            </PerformanceTracedSuspense>
                          }
                        />
                        <Route
                          path="/test/storage"
                          element={<StoragePermissionTester />}
                        />
                        <Route
                          path="/test/edge"
                          element={<EdgeFunctionTester />}
                        />
                        {/* <Route path="/admin" element={
                        <ProtectedRoute>
                          <PerformanceTracedSuspense fallback={<PageLoader type="settings" message="Loading admin panel..." />} name="admin_page">
                            <AdminPage />
                          </PerformanceTracedSuspense>
                        </ProtectedRoute> */}
                        {/* } /> */}
                        <Route
                          path="/business-card"
                          element={
                            <PerformanceTracedSuspense
                              fallback={
                                <PageLoader
                                  type="settings"
                                  message="Loading business card..."
                                />
                              }
                              name="business_card"
                            >
                              <BusinessCardPage />
                            </PerformanceTracedSuspense>
                          }
                        />
                      </Route>
                    </Routes>
                  </TooltipProvider>
                </UploadProvider>
              </LocationProvider>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </PayPalScriptProvider>
  );
};

const App = () => <AppWithPerformance />;

export default App;
